import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import {
  CategoriaBugReport,
  CategoriaBugReportInput,
  criarCategoria,
  atualizarCategoria,
} from '../../services/bugReportService';

interface Props {
  categoria: CategoriaBugReport | null; // null = criar nova
  onClose: () => void;
  onSaved: () => void;
}

export const CategoriaModal: React.FC<Props> = ({ categoria, onClose, onSaved }) => {
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [ordem, setOrdem] = useState(0);
  const [ativo, setAtivo] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (categoria) {
      setNome(categoria.nome);
      setDescricao(categoria.descricao || '');
      setOrdem(categoria.ordem);
      setAtivo(categoria.ativo);
    } else {
      setNome('');
      setDescricao('');
      setOrdem(0);
      setAtivo(true);
    }
  }, [categoria]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (nome.trim().length < 3) {
      alert('Nome muito curto');
      return;
    }

    setSaving(true);
    try {
      const input: CategoriaBugReportInput = {
        nome: nome.trim(),
        descricao: descricao.trim() || undefined,
        ordem,
        ativo,
      };
      if (categoria) {
        await atualizarCategoria(categoria.id, input);
      } else {
        await criarCategoria(input);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      console.error(err);
      alert('Erro: ' + (err?.response?.data?.detail || err?.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg shadow-2xl max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold">
            {categoria ? 'Editar Categoria' : 'Nova Categoria'}
          </h2>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Nome *</label>
            <input
              type="text"
              required
              maxLength={100}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: Leitura Codigo de Barras"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Descricao (ajuda para o auditor)
            </label>
            <textarea
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: Beep nao ocorreu, codigo nao reconhecido..."
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Ordem</label>
              <input
                type="number"
                min={0}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                value={ordem}
                onChange={(e) => setOrdem(Number(e.target.value))}
              />
              <p className="text-[11px] text-gray-500 mt-1">Menor aparece primeiro no dropdown</p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Ativo</label>
              <label className="flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  checked={ativo}
                  onChange={(e) => setAtivo(e.target.checked)}
                  className="h-4 w-4 text-blue-600"
                />
                <span className="text-sm text-gray-700">Categoria ativa</span>
              </label>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-gray-200 bg-gray-50">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </div>
  );
};
